import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import store from './journalStore';

/* A romantic full-screen slideshow + photo manager.
   - auto-plays with soft cross-fade + gentle Ken Burns motion
   - prev/next (arrows, swipe, keyboard), play/pause, progress
   - per-photo actions: move to another date, or remove
   Images are prefetched (current + neighbours) for smooth, flash-free playback. */

const INTERVAL = 4200;
const pad = (n) => String(n).padStart(2, '0');
const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const prettyDate = (ds) => {
  const [y, m, dd] = ds.split('-').map(Number);
  return new Date(y, m - 1, dd).toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
};

export default function Slideshow({ passcode, photos, startIndex = 0, autoplay = false, onClose }) {
  const [list, setList] = useState(photos || []);
  const [index, setIndex] = useState(startIndex);
  const [playing, setPlaying] = useState(autoplay);
  const [editing, setEditing] = useState(false); // false | 'date' | 'caption'
  const [capDraft, setCapDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const urls = useRef(new Map());
  const [, force] = useReducer((x) => x + 1, 0);
  const touchX = useRef(null);

  const len = list.length;
  const i = len ? ((index % len) + len) % len : 0;
  const current = list[i];

  const ensure = useCallback(
    async (id) => {
      if (!id || urls.current.has(id)) return;
      urls.current.set(id, null);
      try {
        const u = await store.photoUrl(passcode, id);
        urls.current.set(id, u);
        force();
      } catch (e) {
        urls.current.delete(id);
      }
    },
    [passcode]
  );

  // prefetch current + neighbours
  useEffect(() => {
    if (!len) return;
    [list[i], list[(i + 1) % len], list[(i - 1 + len) % len]].forEach((p) => p && ensure(p.id));
  }, [i, list, len, ensure]);

  // autoplay (never while editing a photo's date)
  useEffect(() => {
    if (!playing || editing || len < 2) return undefined;
    const t = setTimeout(() => setIndex((v) => v + 1), INTERVAL);
    return () => clearTimeout(t);
  }, [playing, editing, i, len]);

  // revoke object URLs on unmount
  useEffect(
    () => () => {
      urls.current.forEach((u) => u && typeof u === 'string' && u.startsWith('blob:') && URL.revokeObjectURL(u));
      urls.current.clear();
    },
    []
  );

  const go = useCallback((dir) => {
    setPlaying(false);
    setIndex((v) => v + dir);
  }, []);

  // keyboard
  useEffect(() => {
    const onKey = (e) => {
      // don't steal keys while typing in the date/caption editors
      const tag = e.target && e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') {
        if (e.key === 'Escape') setEditing(false);
        return;
      }
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === ' ') {
        e.preventDefault();
        setPlaying((p) => !p);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose]);

  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  const showToast = (m) => {
    setToast(m);
    setTimeout(() => setToast(''), 1800);
  };

  const del = async () => {
    if (!current) return;
    setPlaying(false);
    setBusy(true);
    try {
      await store.delPhoto(passcode, current.id);
      const u = urls.current.get(current.id);
      if (u && u.startsWith('blob:')) URL.revokeObjectURL(u);
      urls.current.delete(current.id);
      const next = list.filter((p) => p.id !== current.id);
      if (!next.length) {
        onClose();
        return;
      }
      setList(next);
      setIndex((v) => Math.min(((v % len) + len) % len, next.length - 1));
    } catch (e) {
      showToast('Could not remove that photo');
    } finally {
      setBusy(false);
    }
  };

  const move = async (newDate) => {
    if (!current || !newDate || newDate === current.date) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await store.editPhoto(passcode, current.id, { date: newDate });
      setList((l) => l.map((p) => (p.id === current.id ? { ...p, date: newDate } : p)));
      setEditing(false);
      showToast(`Moved to ${prettyDate(newDate).replace(/^\w+, /, '')} 💕`);
    } catch (e) {
      showToast('Could not move that photo');
    } finally {
      setBusy(false);
    }
  };

  const saveCaption = async () => {
    if (!current) return;
    const next = capDraft.trim();
    setBusy(true);
    try {
      await store.editPhoto(passcode, current.id, { caption: next });
      setList((l) => l.map((p) => (p.id === current.id ? { ...p, caption: next } : p)));
      setEditing(false);
      showToast(next ? 'Caption saved 💕' : 'Caption removed');
    } catch (e) {
      showToast('Could not save the caption');
    } finally {
      setBusy(false);
    }
  };

  if (!current) return null;
  const url = urls.current.get(current.id);
  const hasAuthor = current.author && current.author !== 'Us';

  return (
    <div className="aj-ss" onClick={onClose}>
      {len > 1 && (
        <div className="aj-ss-bar">
          <div
            className={`aj-ss-bar-fill${playing ? ' run' : ''}`}
            key={`${i}-${playing ? 'p' : 'x'}`}
            style={{ animationDuration: `${INTERVAL}ms` }}
          />
        </div>
      )}

      <button type="button" className="aj-ss-close" onClick={onClose} aria-label="close">
        ×
      </button>

      <div className="aj-ss-stage" onClick={(e) => e.stopPropagation()} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        {url ? (
          <img key={current.id} src={url} alt={current.caption || 'our memory'} className="aj-ss-img" draggable={false} />
        ) : (
          <div className="aj-ss-loading"><span className="aj-ss-spinner" /></div>
        )}

        {len > 1 && (
          <>
            <button type="button" className="aj-ss-nav aj-ss-prev" onClick={() => go(-1)} aria-label="previous">‹</button>
            <button type="button" className="aj-ss-nav aj-ss-next" onClick={() => go(1)} aria-label="next">›</button>
          </>
        )}
      </div>

      <div className="aj-ss-foot" onClick={(e) => e.stopPropagation()}>
        <div className="aj-ss-meta">
          <div className="aj-ss-date">{prettyDate(current.date)}</div>
          {(current.caption || hasAuthor) && (
            <div className="aj-ss-cap">
              {current.caption}
              {current.caption && hasAuthor ? ' · ' : ''}
              {hasAuthor ? current.author : ''}
            </div>
          )}
        </div>

        {editing === 'date' ? (
          <div className="aj-ss-edit">
            <span>Move this photo to:</span>
            <input
              type="date" className="aj-date-input" max={todayYmd()} defaultValue={current.date}
              onChange={(e) => move(e.target.value)} disabled={busy} autoFocus
            />
            <button type="button" className="aj-ss-tool" onClick={() => setEditing(false)} disabled={busy}>cancel</button>
          </div>
        ) : editing === 'caption' ? (
          <form
            className="aj-ss-edit"
            onSubmit={(e) => {
              e.preventDefault();
              saveCaption();
            }}
          >
            <input
              type="text" className="aj-date-input aj-cap-input" value={capDraft} maxLength={140}
              onChange={(e) => setCapDraft(e.target.value)}
              placeholder="say something cute 😏" disabled={busy} autoFocus
            />
            <button type="submit" className="aj-ss-tool" disabled={busy}>save 💕</button>
            <button type="button" className="aj-ss-tool" onClick={() => setEditing(false)} disabled={busy}>cancel</button>
          </form>
        ) : (
          <div className="aj-ss-tools">
            {len > 1 && (
              <button type="button" className="aj-ss-tool" onClick={() => setPlaying((p) => !p)}>
                {playing ? '⏸ Pause' : '▶ Play'}
              </button>
            )}
            <button type="button" className="aj-ss-tool" onClick={() => { setPlaying(false); setCapDraft(current.caption || ''); setEditing('caption'); }} disabled={busy}>✏️ Caption</button>
            <button type="button" className="aj-ss-tool" onClick={() => { setPlaying(false); setEditing('date'); }} disabled={busy}>📅 Move date</button>
            <button type="button" className="aj-ss-tool danger" onClick={del} disabled={busy}>🗑 Remove</button>
          </div>
        )}

        {len > 1 && (
          <div className="aj-ss-dots">
            {len <= 14 ? (
              list.map((p, k) => (
                <button
                  type="button" key={p.id} className={`aj-ss-dot${k === i ? ' on' : ''}`}
                  onClick={() => go(k - i)} aria-label={`photo ${k + 1}`}
                />
              ))
            ) : (
              <span className="aj-ss-counter">{i + 1} / {len}</span>
            )}
          </div>
        )}
      </div>

      {toast && <div className="aj-ss-toast">{toast}</div>}
    </div>
  );
}
